// ════════════════════════════════════════════════════════════════════════════
//  QuitarTituloDialog.tsx
//
//  Quitação do TÍTULO inteiro (paga/recebe TODAS as parcelas em aberto de uma
//  vez), com data e FORMA de pagamento. Distinto da baixa de uma parcela — por
//  isso confirma explicitamente. Serve os dois lados (pagar/receber).
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Wallet, Loader2 } from 'lucide-react';
import { useFormasPagamentoCombo } from '../hooks/useCadastrosFinanceiros';

// Radix Select não aceita value="" — sentinela para "sem forma informada".
const SEM_FORMA = '__none__';

interface QuitarTituloDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (req: { dtPagamento: string; formaPagamentoId?: number }) => void;
  isLoading?: boolean;
  tipo: 'pagar' | 'receber';
  pessoaNome?: string | null;
  saldo: number;
}

const hoje = () => new Date().toISOString().slice(0, 10);
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function QuitarTituloDialog({
  open, onOpenChange, onConfirm, isLoading = false, tipo, pessoaNome, saldo,
}: QuitarTituloDialogProps) {
  const [dtPagamento, setDtPagamento]       = useState(hoje());
  const [formaPagamento, setFormaPagamento] = useState<string>(SEM_FORMA);

  const ehPagar = tipo === 'pagar';
  const { formasPagamento } = useFormasPagamentoCombo();
  const formas = formasPagamento.filter((f) => (ehPagar ? f.permitePagamento : f.permiteRecebimento));

  useEffect(() => {
    if (open) {
      setDtPagamento(hoje());
      setFormaPagamento(SEM_FORMA);
    }
  }, [open]);

  function handleConfirm() {
    onConfirm({
      dtPagamento,
      formaPagamentoId: formaPagamento === SEM_FORMA ? undefined : Number(formaPagamento),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-blue-500" />
            Quitar o título inteiro
          </DialogTitle>
          <DialogDescription>
            {ehPagar
              ? 'Paga TODAS as parcelas em aberto deste título de uma vez. Para pagar só uma, use o botão de pagamento da parcela.'
              : 'Recebe TODAS as parcelas em aberto deste título de uma vez. Para receber só uma, use o botão de recebimento da parcela.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{ehPagar ? 'Fornecedor' : 'Cliente'}</span>
              <span className="font-medium">{pessoaNome || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Saldo do título</span>
              <span className="font-semibold">{brl(saldo)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="dtQuitacaoTitulo">Data do {ehPagar ? 'pagamento' : 'recebimento'}</Label>
              <Input
                id="dtQuitacaoTitulo"
                type="date"
                value={dtPagamento}
                onChange={(e) => setDtPagamento(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="formaQuitacaoTitulo">Forma de {ehPagar ? 'pagamento' : 'recebimento'}</Label>
              <Select value={formaPagamento} onValueChange={setFormaPagamento}>
                <SelectTrigger id="formaQuitacaoTitulo">
                  <SelectValue placeholder="Selecione (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_FORMA}>— Nenhuma —</SelectItem>
                  {formas.map((f) => (
                    <SelectItem key={f.id} value={String(f.id)}>{f.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || !dtPagamento}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Quitar título ({brl(saldo)})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
